<template>
  <div class="container bottom-bar" :class="{'hidden-bottom-row': !isShowBottomRow}" id="bottom-bar" >
    <div v-if="chartInBottomBar && shouldShowBottomChart" class="chart">
      <CanvasChart
        :segments="history.segments"
        :bpm="history.bpm"
        :rpm="history.rpm"
        :ftp="history.ftp"
        :watts="history.watts"
        :showWattsDiagram="true"
        :showHRDiagram="true"
        class="item"
      />
    </div>

    <!-- Interval comment overlay (band at top of screen) -->
    <div v-if="segmentComment.text" class="comment-overlay">
      <span class="comment-text">{{ segmentComment.text }}</span>
    </div>

    <!-- Always show text overlay in center of bottom bar -->
    <div v-if="current.nextIntervalText !== undefined" class="next-overlay-container">
      <span
          class="next-int chart-overlay"
          :class="{
            'blinking': current.time.interval < 5 && current.statusActivity === 'playing',
            'big-change': isBigPowerChange && current.time.interval < 5 && current.statusActivity === 'playing'
          }"
      >
        <template v-if="current.nextIntervalText && current.nextIntervalText.duration">
          <b>{{ current.nextIntervalText.duration }}</b>
          <small style="margin: 0 10px; font-size: 20px;">NEXT INTERVAL</small>
          <b>{{ current.nextIntervalText.target }}W</b>
        </template>
        <template v-else-if="typeof current.nextIntervalText === 'string'">
          Next {{ current.nextIntervalText }}
        </template>
        <template v-else>
          THE LAST SEGMENT
        </template>
      </span>
    </div>

    <div class="target-section">
      <div class="block actions-bar">
        <!-- Toolbar with buttons -->
        <div class="toolbar">
          <div class="toolbar-left">
            <!-- BLE Mode Controls (only show if ?ble=yes query string) -->
            <template v-if="user.bleMode">
              <!-- Bluetooth button (moved to far left) -->
              <button class="toolbar-button" :class="{'active': bluetooth.isConnected}" @click="toggleBluetooth">
                <template v-if="bluetooth.isConnected">
                  <span class="device-count">{{ bluetoothDeviceCount }}</span>
                </template>
                <template v-else>
                  <svg-icon icon="bluetooth"/>
                </template>
              </button>
              
              <!-- Separator -->
              <div class="toolbar-separator"></div>
              
              <!-- Play/Pause button -->
              <button class="toolbar-button" @click="togglePlayPause">
                <svg-icon :icon="current.statusActivity === 'playing' ? 'pause' : 'play'" />
              </button>
              
              <!-- Stop button -->
              <button class="toolbar-button" @click="stopWorkout">
                <svg-icon icon="stop" />
              </button>
              
              <!-- Separator -->
              <div class="toolbar-separator"></div>
              
              <!-- HR+ button -->
              <button class="toolbar-button toolbar-button-text">
                HR+
              </button>
              
              <!-- ERG button -->
              <button class="toolbar-button toolbar-button-text active">
                ERG
              </button>
              
              <!-- SLOPE button -->
              <button class="toolbar-button toolbar-button-text">
                SLOPE
              </button>
              
              <!-- Separator -->
              <div class="toolbar-separator"></div>
            </template>
            
            <!-- YouTube button (always show, but handle auth) -->
            <button class="toolbar-button" :class="{'active': youTubeList}" @click="handleYouTubeClick">
              <svg-icon icon="youtube"/>
            </button>
            
            <!-- Map button (moved from actions-section) - HIDDEN -->
            <!-- <button class="toolbar-button" :class="{'active': isShowRouteMap}" @click="toggleDisplayMap">
              <svg-icon icon="map-route"/>
            </button> -->
            
            <!-- Fullscreen button (moved from actions-section) -->
            <button class="toolbar-button" :class="{'active': isFullscreen}" @click="$emit('onToggleFullscreen')">
              <svg-icon :icon="isFullscreen ? 'fullscreen-off' : 'fullscreen-on'"/>
            </button>
            
            <!-- Sign in button (when not logged in) -->
            <button v-if="user.hasLoaded && !isLogged" class="toolbar-button sign-in-button" @click="signIn">
              <svg-icon icon="sign-in"/>
            </button>
            
            <!-- Chevron button -->
            <button class="toolbar-button chevron-button" :class="{'active': isShowBottomRow}" @click="toggleDisplayBottomRow">
              <svg-icon icon="chevron-down"/>
            </button>
          </div>
          
          <div class="toolbar-right">
            <!-- Speed and Distance (moved from actions-section) -->
            <div class="speed-distance-display">
              <div class="speed-distance-row">
                <span>{{ speedText }}</span>
                <svg-icon icon="speed" />
              </div>
              <div class="speed-distance-row">
                <span>{{ distanceText }}</span>
                <svg-icon icon="ruler" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="block-target block is-large">
        <div class="title">TARGET
          <template v-if="currentZone">- <strong :class="currentZone.class">Z{{ currentZone.number }}</strong></template>
        </div>
        <div class="center-value">{{ current.targetWatts }}</div>
      </div>
      <div class="block-watts block is-large">
        <div class="title">WATTS</div>
        <div class="center-value">{{ current.watts }}</div>
      </div>

      <div class="t-calories block-bottom-row block is-small">
        <div class="title">CALORIES/KILOJOULES</div>
        <div class="center-value">{{ formatValue(total.calories) }}<span class="value-separator"></span>{{ formatValue(total.kilojoules) }}</div>
      </div>
      <div class="i-calories block-bottom-row block is-small">
        <div class="title">STRESS</div>
        <div class="center-value">{{ total.stress }}<span class="value-slash">/</span>{{ plannedTss }}</div>
      </div>

      <div class="t-watts block-bottom-row block is-small">
        <div class="title">TOTAL AVG</div>
        <div class="center-value">{{ current.averageWatts }}</div>
      </div>
      <div class="i-watts block-bottom-row block is-small">
        <div class="title">INTERVAL AVG</div>
        <div class="center-value">{{ current.averageSegmentWatts }}</div>
      </div>
    </div>

    <div class="timing-section">
      <div class="block-interval block is-medium">
        <span class="title"><ArrowItem :style="style(current.time.isIntervalUp)" />INTERVAL</span>
        <p class="center-value">{{ convertTime(current.time.interval) }}</p>
      </div>
      <div class="block-total block is-medium">
        <span class="title"><ArrowItem :style="style(current.time.isTotalUp)" />TOTAL</span>
        <p class="center-value">{{ convertTime(current.time.total) }}</p>
      </div>

      <div class="block-rpm block is-large">
        <span class="title">{{ rpmTitle }}</span>
        <p class="center-value" :class="{'text-red': isNotMatchRPMTarget }">{{ Math.round(current.rpm) }}</p>
        <span v-show="current.rpmTarget.min > 0" class="bottom-value left-bottom-value">
          {{ current.rpmTarget.min }}
        </span>
        <span v-show="current.rpmTarget.max > 0" class="bottom-value right-bottom-value">
          {{ current.rpmTarget.max }}
        </span>
      </div>
      <div class="block-bpm block is-large">
        <span class="title">BPM</span>
        <p class="center-value">{{ current.bpm }}</p>
      </div>

      <div class="block-bottom-row block is-small">
        <div class="title">TOTAL AVG</div>
        <div class="center-value">{{ total.averageRpm }}</div>
      </div>
      <div class="block-bottom-row block is-small">
        <div class="title">INTERVAL AVG</div>
        <div class="center-value">{{ intervalAverageRpm }}</div>
      </div>

      <div class="block-bottom-row block is-small">
        <div class="title">TOTAL AVG</div>
        <div class="center-value">{{ current.averageBpm }}</div>
      </div>
      <div class="block-bottom-row block is-small">
        <div class="title">INTERVAL AVG</div>
        <div class="center-value">{{ intervalAverageBpm }}</div>
      </div>
    </div>

  </div>
</template>

<script>
  import { getTimeLineOfSegment } from '@trainerday/cycling-converter'
  import { SportType } from '@trainerday/ergdb-types'
  import { getActivityTrainingStress, getTrainingStress } from '@trainerday/cycling-metrics'
  import { wattsToFtp } from '@trainerday/cycling-converter/dist/converter/utils/index'
  import CanvasChart from '@/components/CanvasChart'
  import ArrowItem from '@/components/ArrowItem'
  import { mapActions, mapState, mapMutations } from 'vuex'
  import BluetoothService from '@/services/BluetoothService'

  export default {
    name: 'BottomBar',
    components: {
      ArrowItem,
      CanvasChart
    },
    props: {
      isFullscreen: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        segmentComment: {
          text: null,
          intervalTimeoutId: null
        },
        isShowBottomRow: false,
        bluetoothService: null,
        screenWidth: window.innerWidth
      }
    },
    created() {
      const isShowBottomRow = localStorage.getItem('isShowBottomRow')
      if (isShowBottomRow) {
        this.isShowBottomRow = JSON.parse(localStorage.getItem('isShowBottomRow'))
      }

      // Initialize Bluetooth service
      this.bluetoothService = new BluetoothService()
      
      // Set up data callback
      this.bluetoothService.setDataCallback((data) => {
        this.handleBluetoothData(data)
      })
    },
    mounted() {
      // Add resize listener for responsive chart hiding
      window.addEventListener('resize', this.handleResize)
    },
    beforeDestroy() {
      // Remove resize listener
      window.removeEventListener('resize', this.handleResize)
    },
    watch: {
      'current.targetWatts'(newTarget, oldTarget) {
        // Send target power to BLE device when it changes
        if (this.bluetoothService && this.bluetooth.isConnected && newTarget !== oldTarget) {
          console.log(`🎯 Target power changed: ${oldTarget}W -> ${newTarget}W`);
          this.bluetoothService.setTargetPower(newTarget);
        }
      },
      
      'current.segmentText'(val) {
        console.log('📝 segmentText changed:', val)
        // Check for any non-empty value (including single characters)
        if (val && val.toString().trim().length > 0) {
          clearTimeout(this.segmentComment.intervalTimeoutId)
          clearInterval(this.segmentComment.intervalTimeoutId)

          this.segmentComment.text = val
          console.log('📝 Setting comment text:', this.segmentComment.text)

          // Display for (length * 200ms) + 4 seconds, minimum 5 seconds
          const displayTime = Math.max((val.length * 200) + 4000, 5000)
          this.segmentComment.intervalTimeoutId = setTimeout(() => {
            this.segmentComment.text = null
            clearTimeout(this.segmentComment.intervalTimeoutId)
            this.segmentComment.intervalTimeoutId = null
          }, displayTime)
        } else {
          this.segmentComment.text = null
        }
      }
    },
    computed: {
      ...mapState(['history', 'current', 'user', 'bluetooth']),

      chart() {
        return this.user.chart
      },

      userId() {
        return this.user.userId
      },

      speedText() {
        const { speed, params: { units } } = this.current

        if (units === 'metric') {
          return `${Number(Number(speed).toFixed(1))} KPH`
        }
        // One KPH in meters = 1609.34
        return `${Number(Number(speed * 0.621371).toFixed(1))} MPH`
      },

      distanceText() {
        const { distance, params: { units } } = this.current

        if (units === 'metric') {
          return `${(distance / 1000).toFixed(1)} KM`
        }

        // One mile in meters = 1609.34
        const inMiles = Number(distance / 1609.34).toFixed(1)
        return `${Number(inMiles)} MIL`
      },

      sportIcon() {
        if (this.current.sportType === SportType.SWIM) {
          return 'swimming'
        }
        if (this.current.sportType === SportType.ROWING) {
          return 'rowing'
        }
        return 'bike'
      },

      youTubeList() {
        return this.user.youTubeList
      },

      isShowRouteMap() {
        return this.user.showRouteMap
      },

      chartInBottomBar() {
        return this.user.chartInBottomBar
      },
      
      shouldShowBottomChart() {
        // Don't show chart in bottom area if screen width < 1300px and YouTube is active
        if (this.youTubeList && this.screenWidth < 1300) {
          return false
        }
        return true
      },
      isLogged() {
        return this.user.isLogged
      },
      rpmTitle() {
        return this.sportType === 'rowing' ? 'spm'.toUpperCase() : 'rpm'.toUpperCase()
      },

      intervalAverageRpm() {
        if (this.history.rpm.length) {
          return this.getAverageValueOfSegment(this.current.currentSegmentIndex, 'rpm')
        }
        return 0
      },

      intervalAverageBpm() {
        if (this.history.bpm.length) {
          return this.getAverageValueOfSegment(this.current.currentSegmentIndex, 'bpm')
        }
        return 0
      },

      plannedTss() {
        if (this.history.segments && this.history.segments.length && this.current.ftp) {
          return Math.round(getTrainingStress(this.history.segments, this.current.ftp) || 0)
        }
        return 0
      },

      total() {
        const averageRpm = this.history.rpm.length ? Math.round(this.history.rpm.reduce((acc, cur) => acc + cur) / this.history.rpm.length) : 0
        const stress = Math.round(getActivityTrainingStress(this.history.watts, this.current.ftp) || 0)
        
        // Calculate cumulative calories from watts array
        // Each watts sample represents 1 second of data
        const calories = this.history.watts.length ?
          Math.round(this.history.watts.reduce((acc, cur) => acc + cur) / 1000) : 0

        // 1 kcal = 4.184 kJ, so kJ = calories × 4
        const kilojoules = calories * 4
        
        return {
          averageRpm,
          calories,
          stress,
          kilojoules
        }
      },

      isNotMatchRPMTarget() {
        const { rpmTarget: { min, max }, rpm } = this.current
        if (!min && !max) {
          return false
        }
        return !(rpm >= min && rpm <= max)
      },

      currentZone() {
        const { targetWatts, ftp } = this.current
        if (!targetWatts) {
          return null
        }
        const target = wattsToFtp(targetWatts, ftp)
        if (target <= 55) {
          return { number: 1, class: 'zone-1' }
        } else if (target <= 75) {
          return { number: 2, class: 'zone-2' }
        } else if (target <= 90) {
          return { number: 3, class: 'zone-3' }
        } else if (target <= 105) {
          return { number: 4, class: 'zone-4' }
        } else if (target <= 120) {
          return { number: 5, class: 'zone-5' }
        } else {
          return { number: 6, class: 'zone-6' }
        }
      },

      bluetoothDeviceCount() {
        return this.bluetooth.connectedDevices || 0
      },

      isBigPowerChange() {
        // Check if the next interval power change is more than 25%
        const currentTarget = this.current.targetWatts
        const nextTarget = this.current.nextIntervalText?.target
        
        if (!currentTarget || !nextTarget) {
          return false
        }
        
        const percentChange = Math.abs(nextTarget - currentTarget) / currentTarget
        return percentChange > 0.25
      }
    },
    methods: {
      ...mapActions(['showYoutubeFrame']),
      ...mapMutations(['toggleDisplayRouteMap']),

      toggleDisplayMap() {
        if (this.$screenHeight <= 475) {
          return
        }
        const { showRouteMap } = this.user
        const { routeMapBlock: { routeBlockSettings: { isExpanded } } } = this.$parent.$refs
        if (showRouteMap && isExpanded) {
          this.$parent.$refs.routeMapBlock.expandMapToggle()
        }
        this.toggleDisplayRouteMap()
      },

      toggleDisplayBottomRow() {
        this.isShowBottomRow = !this.isShowBottomRow
        localStorage.setItem('isShowBottomRow', this.isShowBottomRow)
      },

      getAverageValueOfSegment(segmentIndex, historyField) {
        if (!this.history.segments || !this.history.segments.length) {
          return 0
        }

        const segmentTimeLine = getTimeLineOfSegment(segmentIndex, this.history.segments)
        const valuesByRange = this.history[historyField].slice(segmentTimeLine[0], segmentTimeLine[1])

        if (valuesByRange.length === 0) {
          return 0
        }
        const average = valuesByRange.reduce((acc, cur) => acc + cur) / valuesByRange.length
        return Math.round(average)
      },

      signIn() {
        window.location.href = 'https://trainerday.com/login?redirect=https://ride.trainerday.com'
      },

      changeFrame() {
        if (this.$screenHeight <= 475) {
          return
        }
        this.$store.dispatch('showYoutubeFrame')
      },

      handleYouTubeClick() {
        if (!this.isLogged) {
          // Show popup for non-logged in users
          this.showSignInPopup()
        } else {
          // If logged in, proceed with normal YouTube functionality
          this.changeFrame()
        }
      },

      showSignInPopup() {
        // Create a simple popup/alert
        const popup = document.createElement('div')
        popup.className = 'signin-popup'
        popup.innerHTML = `
          <div class="signin-popup-content">
            <div class="signin-popup-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                <polyline points="10,17 15,12 10,7"></polyline>
                <line x1="15" y1="12" x2="3" y2="12"></line>
              </svg>
            </div>
            <div class="signin-popup-text">You must sign in first to access YouTube features</div>
            <button class="signin-popup-button" onclick="this.parentElement.parentElement.remove(); window.location.href='https://trainerday.com/login?redirect=https://ride.trainerday.com'">
              Sign In
            </button>
            <button class="signin-popup-close" onclick="this.parentElement.parentElement.remove()">×</button>
          </div>
        `
        document.body.appendChild(popup)
        
        // Auto-remove popup after 5 seconds
        setTimeout(() => {
          if (popup.parentNode) {
            popup.parentNode.removeChild(popup)
          }
        }, 5000)
      },

      style(isUp) {
        let rotate = 0.5
        if (!isUp) {
          rotate = 0
        }
        return { transform: 'rotate(' + rotate + 'turn)' }
      },

      calculateCalories(watts, hours) {
        const val = watts * hours * 3.6
        return (val < 1 ? Number(val.toFixed(2)) : Math.round(val)) || 0
      },

      formatValue(value) {
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + 'k'
        }
        return value
      },

      convertTime(secs) {
        const secNum = parseInt(secs, 10)
        const hours = Math.floor(secNum / 3600)
        const minutes = Math.floor(secNum / 60) % 60
        const seconds = secNum % 60

        return [hours, minutes, seconds]
          .map((v) => (v < 10 ? '0' + v : v))
          .filter((v, i) => v !== '00' || i > 0)
          .join(':')
      },

      togglePlayPause() {
        // Placeholder for play/pause functionality
        console.log('Toggle play/pause')
      },

      stopWorkout() {
        // Placeholder for stop functionality
        console.log('Stop workout')
      },

      async toggleBluetooth() {
        try {
          if (!this.bluetoothService.isAvailable()) {
            alert('Web Bluetooth is not available in this browser. Please use Chrome, Edge, or another compatible browser.')
            return
          }

          if (this.bluetooth.isConnected) {
            // Disconnect
            await this.bluetoothService.disconnect()
            this.$store.commit('setBluetoothConnection', {
              isConnected: false,
              deviceName: null
            })
            console.log('🔷 Bluetooth disconnected')
          } else {
            // Connect
            console.log('🔷 Connecting to Bluetooth device...')
            await this.bluetoothService.connect()
            const status = this.bluetoothService.getConnectionStatus()
            this.$store.commit('setBluetoothConnection', {
              isConnected: status.isConnected,
              deviceName: status.deviceName,
              deviceCount: 1,
              hasControlPoint: status.hasControlPoint
            })
            console.log('🔷 Bluetooth connected to:', status.deviceName)
            if (status.hasControlPoint) {
              console.log('🎮 Device supports power control')
            } else {
              console.log('📖 Device is read-only (no control point)')
            }
          }
        } catch (error) {
          console.error('🔷 Bluetooth toggle error:', error)
          // Update status in case of error
          const status = this.bluetoothService.getConnectionStatus()
          this.$store.commit('setBluetoothConnection', {
            isConnected: status.isConnected,
            deviceName: status.deviceName,
            deviceCount: status.isConnected ? 1 : 0,
            hasControlPoint: status.hasControlPoint || false
          })
        }
      },

      handleBluetoothData(data) {
        console.log('🔷 Received Bluetooth data:', data)
        
        // Update the store with real BLE data
        this.$store.commit('setBluetoothData', {
          power: data.power,
          cadence: data.cadence,
          heartRate: data.heartRate
        })
        
        if (data.power !== undefined) {
          console.log(`📊 BLE Power: ${data.power}W (should override WebSocket)`)
          console.log(`📊 Store BLE Power: ${this.bluetooth.power}W`)
        }
        
        if (data.cadence !== undefined) {
          console.log(`🚴 BLE Cadence: ${data.cadence} RPM (should override WebSocket)`)
          console.log(`🚴 Store BLE Cadence: ${this.bluetooth.cadence} RPM`)
        }
        
        if (data.heartRate !== undefined) {
          console.log(`❤️ BLE Heart Rate: ${data.heartRate} BPM (should override WebSocket)`)
          console.log(`❤️ Store BLE Heart Rate: ${this.bluetooth.heartRate} BPM`)
        }

        // Debug current displayed values
        setTimeout(() => {
          console.log(`🔍 Current displayed - Power: ${this.current.watts}W, RPM: ${this.current.rpm}, BPM: ${this.current.bpm}`)
          console.log(`🔍 BLE connected: ${this.bluetooth.isConnected}, Last update: ${this.bluetooth.lastUpdate}`)
        }, 100)
      },

      handleResize() {
        this.screenWidth = window.innerWidth
      }
    }
  }
</script>

<style lang="scss">
@keyframes comment-ticker-swap {
  0% {
    left: 100%;
  }

  100% {
    left: -300%;
  }
}
.bottom-bar {
  font-family: "Poppins", sans-serif;
  width: 100%;
  display: flex;
  justify-content: right;
  flex-direction: row;
  color: white;
  margin: 0 8px 8px 0;
  height: 100%;
  background-color: #000000;
  position: relative;
  .target-section {
    display: grid;
    grid-template-areas: "com com com com" "target target watts watts" ". . . .";
    grid-template-rows: 30% 40% 22%;
    transition: grid-template-rows 0.5s;
    width: 47%;
    grid-gap: 8px;
    position: relative;
    margin: 8px 4px 8px 8px;
  }

  // Expand sections when no chart in bottom bar
  &:not(:has(.chart)) .target-section {
    width: 55%;
  }

  &:not(:has(.chart)) .timing-section {
    width: 45%;
  }

  .target-section {
    .block-target {
      grid-area: target;
      .title strong {
        margin-left: 5px;
        font-size: 12px;
        color: #eee;
        width: 25px;
        height: 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        &.zone-1 {
          background-color: #4cdd8a80;
        }
        &.zone-2 {
          background-color: #62a7e880;
        }
        &.zone-3 {
          background-color: #8857e480;
        }
        &.zone-4 {
          background-color: #ffc56280;
        }
        &.zone-5 {
          background-color: #fe595973;
        }
        &.zone-6 {
          background-color: #000000;
        }
      }
    }
    .block-watts {
      grid-area: watts;
    }
    .actions-bar {
      grid-area: com;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      max-width: 100%;
      display: flex;
      position: relative;
      .next-int {
        align-self: flex-end;
        color: #7e8192;
        font-size: 16px;
        transform: scale(1.2);
        position: absolute;
        left: 30px;
        bottom: 30px;
        background: rgba(0, 0, 0, 0.8);
        padding: 15px 30px;
        border-radius: 4px;
        z-index: 10;
        transition: all 1s;
        &.blinking {
          animation: blinker 2s linear infinite;
        }
        &.big-change {
          background: rgba(231, 20, 15, 0.3); // Red translucent background
        }
        &.reduced {
          bottom: 30px;
          transform: scale(1);
        }

        @keyframes blinker {
          50% {
            opacity: 0;
          }
        }
      }
    }
  }

.timing-section {
    display: grid;
    gap: 8px;
    width: 47%;
    grid-template-areas: "int int total total" "rpm rpm bpm bpm" ". . . .";
    grid-template-rows: 30% 40% 22%;
    transition: grid-template-rows 0.5s;
    margin: 8px 4px;
    .block-interval {
      grid-area: int;
    }
    .block-total {
      grid-area: total;
    }
    .block-rpm {
      grid-area: rpm;
    }
    .block-bpm {
      grid-area: bpm;
    }
  }


  .chart {
    width: 35%;
    display: flex;
    margin-right: -16px;
    margin-left: 8px;
    position: relative;
  }

  .comment-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.65);
    padding: 40px 32px 20px 32px;
    z-index: 999;
    text-align: center;
    animation: fadeIn 0.3s ease-in-out;
    pointer-events: none;
  }

  .comment-text {
    color: #ffffff;
    font-size: 26px;
    font-weight: 500;
    line-height: 1.4;
    white-space: pre-wrap;
    word-wrap: break-word;
    display: block;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }


  .chart-overlay {
    background: rgba(0, 0, 0, 0.5);
    color: #d4d5d9;
    font-size: 32px;
    padding: 5px 20px 5px 20px;
    border-radius: 8px;
    transition: all 1s;
    white-space: nowrap;
    transform: scale(1.2);
    &.blinking {
      animation: blinker 2s linear infinite;
    }
    &.big-change {
      background: rgba(231, 20, 15, 0.3); // Red translucent background
    }

    @keyframes blinker {
      50% {
        opacity: 0;
      }
    }
  }

  .next-overlay-container {
    position: absolute;
    left: 50%;
    top: calc(25% - 120px);
    transform: translate(-50%, -50%);
    z-index: 10;
    pointer-events: none;
  }

  .block {
    background: #20232f;
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    border-radius: 4px;
    overflow: hidden;
    gap: 2px; // Reduce gap between title and center-value
    .title {
      color: #83858b;
      font-weight: 500;
      font-size: 14px; // Reduced from 16px
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;
      flex-wrap: nowrap;
      padding: 0;
    }
    .center-value {
      font-size: 36px; // Reduced from 42px
      color: #eaeaea;
      margin: 0;
      padding: 0;
    }
    .bottom-value {
      position: absolute;
      font-size: 18px;
      padding: 5px 15px;
      bottom: 0;
    }
    .right-bottom-value {
      right: 0;
    }
    .left-bottom-value {
      left: 0;
    }
    &.is-small {
      gap: 1px; // Small gap
      .title {
        font-size: 10px;
        margin-bottom: 0; // No negative margin
      }
      .center-value {
        font-size: 28px;
        line-height: 27px; // Less compressed
        color: #7e8192;
        margin-top: 0; // No negative margin
      }
    }
    .value-separator {
      display: inline-block;
      width: 1px;
      height: 16px;
      background-color: #4a4d5a;
      margin: 0 8px;
      vertical-align: middle;
      position: relative;
      top: -2px;
    }
    .value-slash {
      font-size: 12px;
      color: #4a4d5a;
      margin: 0 6px;
      vertical-align: top;
    }
    &.is-medium {
      gap: 1px; // Small gap
      .title {
        margin-bottom: 0; // No negative margin
      }
      .center-value {
        font-size: 28px;
        line-height: 27px; // Less compressed
        margin-top: 0; // No negative margin
      }
    }
    &.is-large {
      gap: 1px; // Small gap
      .title {
        font-size: 14px; // Reduce title font size
        margin-bottom: 0; // No negative margin
      }
      .center-value {
        font-size: 60px;
        line-height: 59px;
        margin-top: 0; // No negative margin
      }
    }
    &.speed-distance-block {
      justify-content: center;
      .values {
        margin-bottom: 10px;
      }
    }
  }

  .chevron-link {
    position: absolute;
    bottom: 5%;
    .icon {
      transition: transform 0.3s;
    }
    .icon {
      transform: rotate(180deg);
    }
    &.active {
      .icon {
        transform: rotate(0);
      }
    }
  }

  .text-red {
    color: #e7140f !important;
  }

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: transparent;
    border-radius: 8px;
    padding: 12px 16px;
    margin: 0;
    height: 60px;
    width: 100%;
    box-sizing: border-box;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .toolbar-right {
    display: flex;
    align-items: center;
  }

  .toolbar-button {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 6px;
    padding: 10px 14px;
    color: #83858b;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 40px;
    font-size: 12px;
    font-weight: 500;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #ffffff;
    }

    &.active {
      background: rgba(255, 255, 255, 0.25);
      color: #ffffff;
    }

    .icon {
      width: 16px;
      height: 16px;
    }
  }

  .toolbar-button-text {
    min-width: 50px;
    font-family: "Poppins", sans-serif;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .toolbar-separator {
    width: 1px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.2);
    margin: 0 8px;
  }

  .chevron-button {
    .icon {
      transition: transform 0.3s;
      transform: rotate(180deg);
    }
    
    &.active .icon {
      transform: rotate(0);
    }
  }

  .sign-in-button {
    .icon {
      color: #ff4444 !important;
    }
    
    &:hover .icon {
      color: #ff6666 !important;
    }
  }

  .speed-distance-display {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
    min-width: 0;
    flex-shrink: 0;
    margin-left: 5px;
  }

  .speed-distance-row {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    min-width: 0;

    .icon {
      width: 16px;
      height: 16px;
      color: #83858b;
      flex-shrink: 0;
    }
  }

  .device-count {
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    min-width: 16px;
    text-align: center;
  }

}

.hidden-bottom-row {
  .target-section,
  .timing-section {
    grid-template-rows: 30% 66% 0;
  }
  
  .block.is-large {
    .center-value {
      font-size: 70px;
      line-height: 69px;
    }
  }
}
@media (min-width: 1920px) {
  .bottom-bar .block.is-large {
    gap: 1px; // Small gap
    .title {
      font-size: 22px; // Reduce from 28px
      margin-bottom: 0; // No negative margin
    }
    .center-value {
      font-size: 70px;
      line-height: 69px;
      margin-top: 0; // No negative margin
    }
  }

  .bottom-bar .block.is-medium {
    gap: 1px; // Small gap
    .title {
      font-size: 28px;
      margin-bottom: 0; // No negative margin
    }
    .center-value {
      font-size: 38px;
      line-height: 37px; // Less compressed
      margin-top: 0; // No negative margin
    }
  }

  .bottom-bar .block.is-small {
    gap: 1px; // Small gap
    .title {
      font-size: 14px;
      margin-bottom: 0; // No negative margin
    }
    .center-value {
      font-size: 34px;
      line-height: 33px; // Less compressed
      margin-top: 0; // No negative margin
    }
  }

  .bottom-bar .target-section {
    .actions-bar .comment {
      font-size: 36px;
    }
    .actions-bar .next-int {
      font-size: 24px;
    }
  }

  .bottom-bar .speed-distance-block {
    font-size: 20px;
  }
  
  .hidden-bottom-row {
    .block.is-large {
      .center-value {
        font-size: 80px;
        line-height: 79px;
      }
    }
  }
}

// Sign-in popup styles
.signin-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease-in-out;
}

.signin-popup-content {
  background-color: #20232f;
  border-radius: 8px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-in-out;
}

.signin-popup-icon {
  color: #ff4444;
  margin-bottom: 16px;
  
  svg {
    width: 48px;
    height: 48px;
  }
}

.signin-popup-text {
  color: #ffffff;
  font-size: 16px;
  margin-bottom: 24px;
  line-height: 1.4;
}

.signin-popup-button {
  background-color: #ff4444;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 12px;
  
  &:hover {
    background-color: #ff6666;
    transform: translateY(-1px);
  }
}

.signin-popup-close {
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  color: #83858b;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
